/**
 * Restricted TipTap Editor for Article Titles
 * Allows: bold, italic, underline, font size
 * Disallows: links, headings, images
 */

import React, { useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import FontSize from '@tiptap/extension-font-size';
import './TipTapEditor.css';

export const TipTapTitleEditor = ({ content, onChange, placeholder = 'Article title...' }) => {
  const [isFocused, setIsFocused] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false, // Disable headings
        blockquote: false, // Disable blockquotes
        codeBlock: false, // Disable code blocks
        horizontalRule: false, // Disable horizontal rules
        hardBreak: false, // Disable hard breaks
        bulletList: false, // Disable bullet lists
        orderedList: false, // Disable ordered lists
      }),
      Underline,
      TextStyle,
      FontSize,
    ],
    content: content || '',
    onUpdate: ({ editor }) => {
      if (onChange) {
        const jsonContent = editor.getJSON();
        onChange(jsonContent);
      }
    },
    onFocus: () => setIsFocused(true),
    onBlur: () => setIsFocused(false),
    editorProps: {
      attributes: {
        class: `prose-editor focus:outline-none p-2 text-base transition-all ${
          isFocused ? 'min-h-[80px]' : 'min-h-[40px]'
        }`
      }
    }
  });

  React.useEffect(() => {
    if (editor && content) {
      const currentContent = editor.getJSON();
      const contentChanged = JSON.stringify(currentContent) !== JSON.stringify(content);
      if (contentChanged) {
        editor.commands.setContent(content);
      }
    }
  }, [content, editor]);

  if (!editor) {
    return null;
  }

  return (
    <div className="border-2 border-[#E5E7EB] rounded-lg bg-white">
      <div className="flex items-center gap-2 p-2 border-b border-[#E5E7EB] bg-[#F9FAFB] rounded-t-lg">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`toolbar-btn-enhanced ${editor.isActive('bold') ? 'is-active' : ''}`}
        >
          <strong>B</strong>
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`toolbar-btn-enhanced ${editor.isActive('italic') ? 'is-active' : ''}`}
        >
          <em>I</em>
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={`toolbar-btn-enhanced ${editor.isActive('underline') ? 'is-active' : ''}`}
        >
          <u>U</u>
        </button>
        <div className="toolbar-divider" />
        <select
          onChange={(e) => {
            const size = e.target.value;
            if (size === 'default') {
              editor.chain().focus().unsetFontSize().run();
            } else {
              editor.chain().focus().setFontSize(size).run();
            }
          }}
          className="px-2 py-1 text-sm border border-[#E5E7EB] rounded"
        >
          <option value="default">Default</option>
          <option value="14px">14px</option>
          <option value="16px">16px</option>
          <option value="18px">18px</option>
          <option value="20px">20px</option>
          <option value="22px">22px</option>
          <option value="24px">24px</option>
          <option value="26px">26px</option>
          <option value="28px">28px</option>
          <option value="30px">30px</option>
          <option value="32px">32px</option>
          <option value="36px">36px</option>
          <option value="40px">40px</option>
          <option value="44px">44px</option>
          <option value="48px">48px</option>
          <option value="52px">52px</option>
          <option value="56px">56px</option>
          <option value="60px">60px</option>
          <option value="64px">64px</option>
        </select>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
};

export default TipTapTitleEditor;
